import { Button, Group, ScrollArea, Table } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundMessage } from '../../components/DataTable';
import { LoadingRows } from '../../components/LoadingRows';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { SearchField } from '../../components/SearchField';
import { Title } from '../../components/Title';
import { useModal } from '../../hooks/useModal';
import {
	plainTableDefaults,
	useResponsiveSpace,
} from '../../hooks/useResponsiveSpace';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import { DepartmentRow } from './components/DepartmentRow';
import { NewDepartmentModal } from './components/NewDepartmentModal';
import type { DepartmentDto } from './types/DepartmentDto';
import { KeyedRow } from '../../components/KeyedRow';

export function Departments() {
	const { organisation } = useParams();
	const [searchQuery, setSearchQuery] = useState('');

	const newModal = useModal();

	const departmentsQuery = query<DepartmentDto[]>(
		`${organisation}/departments/search-departments?searchValue=${searchQuery}`,
	);

	const isDataFound = useMemo(() => {
		return (
			departmentsQuery.data?.value && departmentsQuery.data?.value.length > 0
		);
	}, [departmentsQuery.data?.value]);

	const responsiveHeight = useResponsiveSpace(plainTableDefaults);

	return (
		<>
			<NewDepartmentModal
				isOpened={newModal.isOpen}
				close={newModal.close}
				triggerListUpdate={() => {
					departmentsQuery.remove();
					departmentsQuery.refetch();
				}}
			/>
			<PageHeader title={'Departments'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search departments...'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<Button
						color={'medepos.1'}
						variant={'filled'}
						onClick={() => newModal.open()}
						px={'xs'}
					>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<Paper>
				<ScrollArea w={'100%'} h={responsiveHeight}>
					<Table striped={isDataFound} stickyHeader>
						<Table.Thead>
							<KeyedRow>
								<Table.Th w={'15vw'}>
									<Title label={'Name'} />
								</Table.Th>
								<Table.Th w={'10vw'}>
									<Title label={'Group'} />
								</Table.Th>
								<Table.Th>
									<Title label={'Should Display'} />
								</Table.Th>
								<Table.Th w={'4vw'} />
							</KeyedRow>
						</Table.Thead>
						<Table.Tbody>
							{departmentsQuery.isLoading || departmentsQuery.isRefetching ? (
								<LoadingRows rows={30} columnSpan={8} />
							) : (
								<>
									{isDataFound ? (
										<>
											{departmentsQuery.data?.value.map((department) => (
												<DepartmentRow
													key={department.id}
													department={department}
													triggerListUpdate={() => {
														departmentsQuery.remove();
														departmentsQuery.refetch();
													}}
												/>
											))}
										</>
									) : (
										<KeyedRow>
											<Table.Td colSpan={4}>
												<NoDataFoundMessage />
											</Table.Td>
										</KeyedRow>
									)}
								</>
							)}
						</Table.Tbody>
					</Table>
				</ScrollArea>
			</Paper>
		</>
	);
}
